import type { ReactNode } from 'react';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { UnsavedChangesProvider } from './components/UnsavedChanges';
import { cssVariablesResolver, theme } from './theme';

/**
 * Everything the UI needs above the router: the theme, the colour scheme, the notifications outlet
 * and the guard that asks before somebody walks away from an unsaved form. The app and the tests
 * both mount this, so a test can never render against a different theme than the one people use.
 */
export function AppProviders({
  children,
  env = 'default',
}: {
  children: ReactNode;
  /** 'test' turns off Mantine's transitions and portals, which keeps test output predictable. */
  env?: 'default' | 'test';
}) {
  return (
    <MantineProvider
      theme={theme}
      cssVariablesResolver={cssVariablesResolver}
      defaultColorScheme="auto"
      env={env}
    >
      <Notifications position="bottom-right" />
      {/*
       * Above the router rather than on a screen: what it guards is somebody leaving one, and the
       * screen being left has stopped rendering by the time that matters.
       */}
      <UnsavedChangesProvider>{children}</UnsavedChangesProvider>
    </MantineProvider>
  );
}
